import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import NavHeader from '../components/NavHeader';
import ClassificationReasoning from '../components/ClassificationReasoning';
import AllocationsPanel from '../components/AllocationsPanel';
import {
  fetchInvoice,
  fetchVendors,
  fetchProjects,
  updateInvoiceStatus,
  createApprovalRequest,
  batchLuxembourg,
  getPendingApprovals,
  respondToApproval,
  createVendor,
  updateInvoiceFields,
  deleteInvoice,
  fetchGlPreview,
  fetchInvoiceCandidates,
  fetchSettings,
  fetchInvoiceVatSection,
  createVendorBankAccount,
  updateVendorBankAccount,
} from '../api';
import { fetchLines } from '../budget/api';

const STATUS_OPTIONS = [
  { value: 'received', label: 'Received' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'submitted_to_ap', label: 'Submitted to AP' },
  { value: 'payment_processing', label: 'Payment Processing' },
  { value: 'posted_in_sap', label: 'Posted in SAP' },
  { value: 'reconciled', label: 'Reconciled' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_COLORS = {
  received: '#90caf9',
  under_review: '#fff176',
  pending_approval: '#ffb74d',
  approved: '#81c784',
  submitted_to_ap: '#4fc3f7',
  payment_processing: '#ba68c8',
  posted_in_sap: '#4db6ac',
  reconciled: '#66bb6a',
  rejected: '#ef5350',
};

// Special Tax Handling — mutually-exclusive selector values. "None" maps to
// neither is_vat nor is_luxembourg; the selector clears the other flag when
// one is chosen (backend enforces exclusivity too).
const TAX_MODES = [
  { value: 'none', label: 'None' },
  { value: 'vat', label: 'VAT' },
  { value: 'luxembourg', label: 'Luxembourg' },
];

function formatCurrency(amount, currency) {
  if (amount == null) return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    // Invalid currency code — fall back to plain number
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
}

function formatDateTime(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleString();
}

// Inconspicuous "Why?" chip+popover for free-text explanations, styled to
// match ClassificationReasoning so the page reads consistently. Used for the
// VAT threshold decision and the Luxembourg detection signal.
function ReasonChip({ kind, text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  return (
    <span className="classification-reasoning" data-kind={kind}>
      <button
        type="button"
        className="classification-reasoning-chip"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide' : 'Why?'}
      </button>
      {expanded && <div className="classification-reasoning-panel">{text}</div>}
    </span>
  );
}

function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useUser();

  // Return to wherever the user came from (usually the board, but not always).
  // `location.key === 'default'` means this was a direct load with no in-app
  // history to pop, so fall back to the board.
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/board');
  };

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Approval state
  const [sendingApproval, setSendingApproval] = useState(false);
  const [batchingLux, setBatchingLux] = useState(false);
  const [approvalRequest, setApprovalRequest] = useState(null);
  const [respondingApproval, setRespondingApproval] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState(null);

  // Inline editing state
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [vendors, setVendors] = useState([]);
  const [savingFields, setSavingFields] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  // Click-to-edit: the field key the user clicked, so we can focus it once the
  // editor renders. Cleared after focus is applied.
  const [focusField, setFocusField] = useState(null);
  const editFieldRefs = useRef({});

  // Overflow (⋯) menu in the sticky header — holds the Delete action.
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);

  // Project selection state
  const [projects, setProjects] = useState([]);
  const [candidateProjects, setCandidateProjects] = useState([]);

  // Budget lines for the budget-line selector in edit mode
  const [budgetLines, setBudgetLines] = useState([]);

  // GL preview modal state
  const [glPreview, setGlPreview] = useState(null);
  const [showGlModal, setShowGlModal] = useState(false);
  const [pendingGlSave, setPendingGlSave] = useState(null);
  const [aiInstruction, setAiInstruction] = useState('');

  // Extracted text display
  const [showExtractedText, setShowExtractedText] = useState(false);
  const [extractedTextExpanded, setExtractedTextExpanded] = useState(false);

  // VAT section state (populated when invoice.is_vat === true)
  const [vatSection, setVatSection] = useState(null);
  const [vatLoading, setVatLoading] = useState(false);

  // Document preview state
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [docPreviewLoading, setDocPreviewLoading] = useState(true);
  const [excelHtml, setExcelHtml] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  // Resizable preview pane (width as percentage of split container)
  const DEFAULT_PREVIEW_WIDTH = 40;
  const MIN_PREVIEW_WIDTH = 20;
  const MAX_PREVIEW_WIDTH = 80;
  const splitRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(() => {
    const saved = parseFloat(localStorage.getItem('invoicePreviewWidth'));
    if (!isNaN(saved) && saved >= MIN_PREVIEW_WIDTH && saved <= MAX_PREVIEW_WIDTH) {
      return saved;
    }
    return DEFAULT_PREVIEW_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);

  const startResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e) => {
      if (!splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const rightEdgeOffset = rect.right - e.clientX;
      let pct = (rightEdgeOffset / rect.width) * 100;
      if (pct < MIN_PREVIEW_WIDTH) pct = MIN_PREVIEW_WIDTH;
      if (pct > MAX_PREVIEW_WIDTH) pct = MAX_PREVIEW_WIDTH;
      setPreviewWidth(pct);
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing]);

  useEffect(() => {
    localStorage.setItem('invoicePreviewWidth', String(previewWidth));
  }, [previewWidth]);

  const togglePreviewWidth = () => {
    // Toggle between default and expanded (~70%) for quick access
    setPreviewWidth((w) => (w >= 60 ? DEFAULT_PREVIEW_WIDTH : 70));
  };

  const loadInvoice = useCallback(async () => {
    try {
      const res = await fetchInvoice(id);
      setInvoice(res.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load invoice');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Load vendors for the combobox
  useEffect(() => {
    fetchVendors().then(res => setVendors(res.data)).catch(() => {});
  }, []);

  // Load projects for classification
  useEffect(() => {
    fetchProjects().then(res => setProjects(res.data)).catch(() => {});
  }, []);

  // Load setting for extracted text visibility
  useEffect(() => {
    fetchSettings()
      .then(res => { if (res.data?.show_extracted_text) setShowExtractedText(true); })
      .catch(() => {});
  }, []);

  // Load budget lines for the edit-mode selector. FY defaults from the
  // invoice's fiscal_year; falls back to current FY (Feb-start: month >= 2
  // means the year is the current FY, otherwise it's the prior year).
  useEffect(() => {
    if (!invoice) return;
    const now = new Date();
    const currentFy = now.getMonth() >= 1 ? now.getFullYear() : now.getFullYear() - 1;
    const fy = invoice.fiscal_year != null ? invoice.fiscal_year : currentFy;
    fetchLines(fy)
      .then(res => setBudgetLines(res.data?.lines || []))
      .catch(() => setBudgetLines([]));
  }, [invoice?.id, invoice?.fiscal_year]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load candidate projects when invoice loads
  useEffect(() => {
    if (invoice?.id) {
      fetchInvoiceCandidates(invoice.id)
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : [];
          setCandidateProjects(data);
        })
        .catch(() => setCandidateProjects([]));
    }
  }, [invoice?.id]);

  // Load VAT section aggregate for any invoice — the panel is rendered
  // unconditionally now so we always need the bank account / threshold payload.
  const loadVatSection = useCallback(async () => {
    if (!invoice?.id) {
      setVatSection(null);
      return;
    }
    setVatLoading(true);
    try {
      const res = await fetchInvoiceVatSection(invoice.id);
      setVatSection(res.data);
    } catch {
      setVatSection(null);
    } finally {
      setVatLoading(false);
    }
  }, [invoice?.id]);

  useEffect(() => {
    loadVatSection();
  }, [loadVatSection]);

  // Honor URL hash deep-links (e.g. /invoice/{id}#vat-section from the VAT
  // dashboard). Wait until the invoice has loaded so the anchor element
  // is in the DOM before we scroll.
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash || !invoice) return;
    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [invoice]);

  // Click-to-edit: once the editor renders, focus the field the user clicked.
  useEffect(() => {
    if (!editing || !focusField) return;
    const el = editFieldRefs.current[focusField];
    if (el) {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
    setFocusField(null);
  }, [editing, focusField]);

  // Close the overflow (⋯) menu on any outside click.
  useEffect(() => {
    if (!overflowOpen) return;
    const onDocClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [overflowOpen]);

  // Load pending approval for this invoice if user is approver
  useEffect(() => {
    if (currentUser?.role === 'approver' && invoice?.status === 'pending_approval') {
      getPendingApprovals().then((res) => {
        const match = res.data.find(
          (a) => String(a.invoice_id) === String(id)
        );
        setApprovalRequest(match || null);
      }).catch(() => {});
    }
  }, [currentUser?.role, invoice?.status, id]);

  // Fetch Excel preview HTML when an Excel doc is selected
  const selectedDocForExcel = (invoice?.documents || [])[selectedDocIndex] || null;
  useEffect(() => {
    if (!selectedDocForExcel) return;
    const filename = selectedDocForExcel.original_filename || selectedDocForExcel.filename || '';
    const ext = filename.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setExcelHtml('');
      return;
    }
    let cancelled = false;
    setExcelLoading(true);
    setExcelHtml('');
    fetch(`/api/invoices/${id}/documents/${selectedDocForExcel.id}/preview-excel`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load Excel preview');
        return res.text();
      })
      .then((html) => { if (!cancelled) setExcelHtml(html); })
      .catch(() => { if (!cancelled) setExcelHtml('<p style="color:#999;text-align:center;">Could not load Excel preview</p>'); })
      .finally(() => { if (!cancelled) setExcelLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDocForExcel, id]);

  // Inline editing handlers. `fieldToFocus` (optional) is the editFields key
  // the user clicked in read mode — we focus it once the editor renders.
  const startEditing = (fieldToFocus = null) => {
    const bank = vatSection?.bank_account || {};
    setEditFields({
      vendor_id: invoice.vendor_id || '',
      project_id: invoice.project_id || '',
      amount: invoice.amount || '',
      currency: invoice.currency || 'USD',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      jurisdiction: invoice.jurisdiction || '',
      payment_date: invoice.payment_date || '',
      gl_company_code: invoice.gl_company_code || '',
      gl_cost_center: invoice.gl_cost_center || '',
      gl_account: invoice.gl_account || '',
      gl_internal_order: invoice.gl_internal_order || '',
      invoice_reference: invoice.invoice_reference || '',
      comments: invoice.comments || '',
      is_vat: !!invoice.is_vat,
      is_luxembourg: !!invoice.is_luxembourg,
      fiscal_year: invoice.fiscal_year != null ? String(invoice.fiscal_year) : '',
      budget_line_id: invoice.budget_line_id || '',
      vat_amount: vatSection?.vat_amount != null ? vatSection.vat_amount : '',
      bank_account_name: bank.account_name || '',
      bank_name: bank.bank_name || '',
      bank_location: bank.bank_location || '',
      bank_swift_code: bank.swift_code || '',
      bank_aba_number: bank.aba_number || '',
      bank_account_number: bank.account_number || '',
      bank_iban: bank.iban || '',
      bank_sort_code: bank.sort_code || '',
      bank_currency: bank.currency || '',
    });
    setNewVendorName('');
    setEditing(true);
    setOverflowOpen(false);
    if (fieldToFocus) setFocusField(fieldToFocus);
  };

  // Click-to-edit affordance for read-mode values. Wraps the display node so a
  // click enters edit mode and focuses the matching field.
  const editable = (fieldKey, children) => {
    if (editing) return children;
    return (
      <span
        className="editable-value"
        role="button"
        tabIndex={0}
        title="Click to edit"
        onClick={() => startEditing(fieldKey)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEditing(fieldKey);
          }
        }}
      >
        {children}
        <span className="editable-pencil" aria-hidden="true">&#9998;</span>
      </span>
    );
  };

  // Special Tax Handling mode derived from is_vat / is_luxembourg. In edit mode
  // it reflects pending editFields; in read mode it reflects the saved invoice.
  const currentTaxMode = (() => {
    const vat = editing ? !!editFields.is_vat : !!invoice?.is_vat;
    const lux = editing ? !!editFields.is_luxembourg : !!invoice?.is_luxembourg;
    if (vat) return 'vat';
    if (lux) return 'luxembourg';
    return 'none';
  })();

  const setTaxMode = (mode) => {
    setEditFields({
      ...editFields,
      is_vat: mode === 'vat',
      is_luxembourg: mode === 'luxembourg',
    });
  };

  // Luxembourg detection reasoning for the "Why?" popover. Canonical source is
  // vatSection.lux_detection (latest luxembourg_detected event metadata, or
  // null) — confirmed shape {signal, match}, e.g. {signal: "pdf_text_substring",
  // match: "CHS"}. The older keys are kept as a defensive fallback; when no
  // signal is present we explain the known CHS-substring rule so the chip
  // always has content.
  const luxDetectionReason = (() => {
    const raw =
      vatSection?.lux_detection ??
      vatSection?.luxembourg_detection ??
      vatSection?.lux_signal ??
      null;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (raw && typeof raw === 'object') {
      if (typeof raw.reason === 'string' && raw.reason.trim()) return raw.reason.trim();
      // Confirmed {signal, match} shape → friendly prose.
      if (raw.signal === 'pdf_text_substring' && raw.match) {
        return `Auto-flagged: the extracted PDF text contains "${raw.match}".`;
      }
      const entries = Object.entries(raw)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
      if (entries.length) return entries.join('\n');
    }
    return 'Auto-flagged: the extracted PDF text contains "CHS". Toggle manually in edit mode if this is wrong.';
  })();

  // Threshold-decision reasoning for the VAT "Why?" popover.
  const thresholdReason = (() => {
    const td = vatSection?.threshold_decision;
    if (!td || typeof td !== 'object') return null;
    if (typeof td.reason === 'string' && td.reason.trim()) {
      const extras = Object.entries(td)
        .filter(([k, v]) => k !== 'reason' && v != null && v !== '')
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
      return extras.length ? `${td.reason.trim()}\n${extras.join('\n')}` : td.reason.trim();
    }
    const entries = Object.entries(td)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
    return entries.length ? entries.join('\n') : null;
  })();

  const buildChangedFields = () => {
    const fields = {};
    if (editFields.vendor_id && editFields.vendor_id !== invoice.vendor_id) {
      fields.vendor_id = editFields.vendor_id;
    }
    if (editFields.amount && String(editFields.amount) !== String(invoice.amount)) {
      fields.amount = editFields.amount;
    }
    if (editFields.currency && editFields.currency !== invoice.currency) {
      fields.currency = editFields.currency;
    }
    if (editFields.invoice_number && editFields.invoice_number !== invoice.invoice_number) {
      fields.invoice_number = editFields.invoice_number;
    }
    if (editFields.invoice_date && editFields.invoice_date !== invoice.invoice_date) {
      fields.invoice_date = editFields.invoice_date;
    }
    if (editFields.project_id && editFields.project_id !== invoice.project_id) {
      fields.project_id = editFields.project_id;
    }
    if (editFields.jurisdiction !== (invoice.jurisdiction || '')) {
      fields.jurisdiction = editFields.jurisdiction || null;
    }
    if (editFields.payment_date !== (invoice.payment_date || '')) {
      fields.payment_date = editFields.payment_date || null;
    }
    if (editFields.gl_company_code !== (invoice.gl_company_code || '')) {
      fields.gl_company_code = editFields.gl_company_code || null;
    }
    if (editFields.gl_cost_center !== (invoice.gl_cost_center || '')) {
      fields.gl_cost_center = editFields.gl_cost_center || null;
    }
    if (editFields.gl_account !== (invoice.gl_account || '')) {
      fields.gl_account = editFields.gl_account || null;
    }
    if (editFields.gl_internal_order !== (invoice.gl_internal_order || '')) {
      fields.gl_internal_order = editFields.gl_internal_order || null;
    }
    if (editFields.invoice_reference !== (invoice.invoice_reference || '')) {
      fields.invoice_reference = editFields.invoice_reference || null;
    }
    if (editFields.comments !== (invoice.comments || '')) {
      fields.comments = editFields.comments || null;
    }
    if (editFields.is_vat !== !!invoice.is_vat) {
      fields.is_vat = !!editFields.is_vat;
    }
    if (editFields.is_luxembourg !== !!invoice.is_luxembourg) {
      fields.is_luxembourg = !!editFields.is_luxembourg;
    }
    const editedFy = editFields.fiscal_year === '' || editFields.fiscal_year == null
      ? null
      : parseInt(editFields.fiscal_year, 10);
    const existingFy = invoice.fiscal_year != null ? invoice.fiscal_year : null;
    if (editedFy !== existingFy) {
      fields.fiscal_year = editedFy;
    }
    const editedBl = editFields.budget_line_id === '' ? null : (editFields.budget_line_id || null);
    const existingBl = invoice.budget_line_id || null;
    if (editedBl !== existingBl) {
      fields.budget_line_id = editedBl;
    }
    const editedVat = editFields.vat_amount === '' || editFields.vat_amount == null
      ? null
      : String(editFields.vat_amount);
    const existingVat = vatSection?.vat_amount != null ? String(vatSection.vat_amount) : null;
    if (editedVat !== existingVat) {
      fields.vat_amount = editedVat;
    }
    return fields;
  };

  const BANK_FIELD_MAP = {
    bank_account_name: 'account_name',
    bank_name: 'bank_name',
    bank_location: 'bank_location',
    bank_swift_code: 'swift_code',
    bank_aba_number: 'aba_number',
    bank_account_number: 'account_number',
    bank_iban: 'iban',
    bank_sort_code: 'sort_code',
    bank_currency: 'currency',
  };

  const buildBankAccountChanges = () => {
    const current = vatSection?.bank_account || {};
    const changed = {};
    for (const [editKey, apiKey] of Object.entries(BANK_FIELD_MAP)) {
      const v = editFields[editKey];
      const currentVal = current[apiKey] || '';
      if ((v || '') !== (currentVal || '')) {
        changed[apiKey] = v ? v : null;
      }
    }
    return changed;
  };

  const hasAnyBankValue = () => {
    return Object.keys(BANK_FIELD_MAP).some((k) => {
      const v = editFields[k];
      return v != null && String(v).trim() !== '';
    });
  };

  const doSaveFields = async (fields, updateGlFields, aiInstructionText) => {
    setSavingFields(true);
    try {
      const payload = { ...fields };

      // Bank account: create or update first, then reference from invoice PATCH.
      // Only relevant when user marks the invoice as VAT (or was already VAT
      // and tweaked bank details). InvoiceResponse doesn't expose
      // vendor_bank_account_id; we use vatSection.bank_account.id as proxy.
      const existingBankId = vatSection?.bank_account?.id || null;
      const isVatAfter = payload.is_vat !== undefined ? payload.is_vat : !!invoice.is_vat;
      if (isVatAfter) {
        const bankChanges = buildBankAccountChanges();
        if (existingBankId) {
          if (Object.keys(bankChanges).length > 0) {
            await updateVendorBankAccount(existingBankId, bankChanges);
          }
        } else if (hasAnyBankValue()) {
          const bankBody = { vendor_id: invoice.vendor_id };
          for (const [editKey, apiKey] of Object.entries(BANK_FIELD_MAP)) {
            const v = editFields[editKey];
            if (v != null && String(v).trim() !== '') {
              bankBody[apiKey] = v;
            }
          }
          const res = await createVendorBankAccount(bankBody);
          if (res?.data?.id) {
            payload.vendor_bank_account_id = res.data.id;
          }
        }
      }

      if (updateGlFields !== undefined) {
        payload.update_gl_fields = updateGlFields;
      }
      if (aiInstructionText && aiInstructionText.trim()) {
        payload.ai_instruction = aiInstructionText.trim();
      }
      if (Object.keys(payload).length > 0) {
        await updateInvoiceFields(id, payload);
      }
      setEditing(false);
      loadInvoice();
      loadVatSection();
    } catch (err) {
      setStatusError(err.response?.data?.detail || 'Failed to save fields');
    } finally {
      setSavingFields(false);
    }
  };

  const handleSaveFields = async () => {
    const fields = buildChangedFields();
    const bankChanges = buildBankAccountChanges();
    const needsNewBank =
      !invoice.vendor_bank_account_id && !!editFields.is_vat && hasAnyBankValue();
    if (
      Object.keys(fields).length === 0 &&
      Object.keys(bankChanges).length === 0 &&
      !needsNewBank
    ) {
      setEditing(false);
      return;
    }

    // If project changed, check GL preview first
    if (fields.project_id) {
      setSavingFields(true);
      try {
        const vendorId = editFields.vendor_id || invoice.vendor_id;
        const previewRes = await fetchGlPreview(id, fields.project_id, vendorId);
        const preview = previewRes.data;
        // Always show modal for project changes (GL confirmation + AI instruction opportunity)
        setPendingGlSave(fields);
        setGlPreview(preview);
        setShowGlModal(true);
        setSavingFields(false);
        return;
      } catch (err) {
        setStatusError(err.response?.data?.detail || 'Failed to save fields');
        setSavingFields(false);
      }
      return;
    }

    // No project change — save normally
    await doSaveFields(fields);
  };

  const handleGlModalChoice = async (updateGlFields) => {
    setShowGlModal(false);
    setGlPreview(null);
    const instruction = aiInstruction;
    setAiInstruction('');
    if (pendingGlSave) {
      await doSaveFields(pendingGlSave, updateGlFields, instruction);
      setPendingGlSave(null);
    }
  };

  const handleSendForApproval = async () => {
    if (!invoice?.owner_id && !invoice?.owner_name) return;
    setSendingApproval(true);
    setApprovalMessage(null);
    try {
      await createApprovalRequest(id);
      setApprovalMessage({ type: 'success', text: 'Sent to owner for approval' });
      loadInvoice();
    } catch (err) {
      // FastAPI returns `detail` as a string (HTTPException) OR an array of
      // validation objects (422). Never drop a raw array/object into React —
      // it throws "Objects are not valid as a React child".
      const detail = err.response?.data?.detail;
      const text = Array.isArray(detail)
        ? (detail.map((d) => d?.msg).filter(Boolean).join('; ') || 'Validation error')
        : (typeof detail === 'string' ? detail : 'Failed to send for approval');
      setApprovalMessage({ type: 'error', text });
    } finally {
      setSendingApproval(false);
    }
  };

  const handleBatchAsLuxembourg = async () => {
    setBatchingLux(true);
    setApprovalMessage(null);
    try {
      await batchLuxembourg(id);
      setApprovalMessage({ type: 'success', text: 'Batched as Luxembourg' });
      loadInvoice();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = Array.isArray(detail)
        ? (detail.map((d) => d?.msg).filter(Boolean).join('; ') || 'Validation error')
        : (typeof detail === 'string' ? detail : 'Failed to batch as Luxembourg');
      setApprovalMessage({ type: 'error', text });
    } finally {
      setBatchingLux(false);
    }
  };

  const handleApprovalRespond = async (action) => {
    if (!approvalRequest) return;
    setRespondingApproval(true);
    setApprovalMessage(null);
    try {
      await respondToApproval(approvalRequest.id, action, action === 'rejected' ? (rejectNotes || null) : null);
      setApprovalMessage({ type: 'success', text: action === 'approved' ? 'Invoice approved' : 'Invoice rejected' });
      setApprovalRequest(null);
      setShowRejectForm(false);
      setRejectNotes('');
      loadInvoice();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = Array.isArray(detail)
        ? (detail.map((d) => d?.msg).filter(Boolean).join('; ') || 'Validation error')
        : (typeof detail === 'string' ? detail : 'Action failed');
      setApprovalMessage({ type: 'error', text });
    } finally {
      setRespondingApproval(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === invoice.status) return;
    setStatusUpdating(true);
    setStatusError(null);
    try {
      await updateInvoiceStatus(id, newStatus);
      await loadInvoice();
    } catch (err) {
      setStatusError(err.response?.data?.detail || 'Status change failed');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDeleteInvoice = async () => {
    setOverflowOpen(false);
    if (!window.confirm('Are you sure you want to delete this invoice? This cannot be undone.')) {
      return;
    }
    try {
      await deleteInvoice(id);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  if (loading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner">Loading invoice...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-loading">
        <div className="error-message">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/board')}>
          Back to Board
        </button>
      </div>
    );
  }

  if (!invoice) return null;

  const documents = invoice.documents || [];
  const selectedDoc = documents[selectedDocIndex] || null;

  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop().toLowerCase();
  };

  const isPreviewable = (filename) => {
    const ext = getFileExtension(filename);
    return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  const isImage = (filename) => {
    const ext = getFileExtension(filename);
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  const isExcel = (filename) => {
    const ext = getFileExtension(filename);
    return ['xlsx', 'xls', 'csv'].includes(ext);
  };

  const getPreviewUrl = (doc) => {
    if (!doc) return '';
    return `/api/invoices/${id}/documents/${doc.id}/preview`;
  };

  // Only the standard AP statuses carry a friendly label + color. Tax-flow
  // statuses (lux_open, vat_open, …) aren't shown as a raw enum badge here —
  // those invoices are tracked via their dedicated dashboards/package cards.
  const statusOption = STATUS_OPTIONS.find((s) => s.value === invoice.status);
  const statusLabel = statusOption?.label;
  const inIntakeStatus =
    invoice.status === 'received' || invoice.status === 'under_review';
  const isProcessor = currentUser?.role === 'processor';
  // The "Batch as Luxembourg" confirm gate may be actioned by the processor
  // (Sandra) or an admin (Sandra is an admin in some environments).
  const canBatchLux = ['processor', 'admin'].includes(currentUser?.role);
  // Luxembourg invoices use the "Batch as Luxembourg" confirm gate instead of
  // the approval queue, so the normal "Send for Approval" action is hidden for
  // them. The Lux button is shown to everyone in intake status but only
  // enabled for a processor/admin.
  const canSendForApproval =
    isProcessor && inIntakeStatus && !invoice.is_luxembourg;
  const showBatchLuxembourg =
    !!invoice.is_luxembourg && invoice.status === 'under_review';
  const hasOwner = !!(invoice.owner_id || invoice.owner_name);
  const canDecideApproval =
    currentUser?.role === 'approver' &&
    invoice.status === 'pending_approval' &&
    approvalRequest;

  return (
    <div className="detail-page">
      <NavHeader />

      <div className="detail-content">
        <div
          className={`invoice-detail-split${isResizing ? ' is-resizing' : ''}`}
          ref={splitRef}
        >
        <div
          className="invoice-detail-left"
          style={{ flex: `0 0 ${100 - previewWidth}%` }}
        >
        <div className="detail-main">
          {/* Sticky action bar — invoice number + status, Edit/Save/Cancel,
              the single role/status context action, and the ⋯ overflow menu
              (Delete). Stays visible while the left column scrolls. */}
          <div className="detail-sticky-bar">
            <div className="detail-sticky-title">
              <button
                type="button"
                className="detail-back-btn"
                onClick={goBack}
                aria-label="Go back"
                title="Back"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="detail-sticky-number">
                Invoice #{invoice.invoice_number || invoice.id}
              </span>
              {statusOption && (
                <span
                  className="status-badge-large"
                  style={{ backgroundColor: STATUS_COLORS[invoice.status] }}
                >
                  {statusLabel}
                </span>
              )}
            </div>

            <div className="detail-sticky-actions">
              {!editing ? (
                <button className="btn-edit" onClick={() => startEditing()}>Edit</button>
              ) : (
                <>
                  <button className="btn-save" onClick={handleSaveFields} disabled={savingFields}>
                    {savingFields ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn-cancel" onClick={() => setEditing(false)} disabled={savingFields}>
                    Cancel
                  </button>
                </>
              )}

              {!editing && canSendForApproval && (
                <button
                  className="btn btn-approve"
                  onClick={handleSendForApproval}
                  disabled={!hasOwner || sendingApproval}
                  title={hasOwner ? undefined : 'Assign an owner first'}
                >
                  {sendingApproval ? 'Sending...' : 'Send for Approval'}
                </button>
              )}

              {!editing && showBatchLuxembourg && (
                <button
                  className="btn btn-approve"
                  onClick={handleBatchAsLuxembourg}
                  disabled={!canBatchLux || batchingLux}
                  title={
                    canBatchLux
                      ? 'Confirm as Luxembourg and add to the quarterly batch'
                      : 'Only a processor or admin can batch Luxembourg invoices'
                  }
                >
                  {batchingLux ? 'Batching...' : 'Batch as Luxembourg'}
                </button>
              )}

              {!editing && canDecideApproval && !showRejectForm && (
                <>
                  <button
                    className="btn btn-approve"
                    onClick={() => handleApprovalRespond('approved')}
                    disabled={respondingApproval}
                  >
                    {respondingApproval ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setShowRejectForm(true)}
                    disabled={respondingApproval}
                  >
                    Reject
                  </button>
                </>
              )}

              <div className="detail-overflow" ref={overflowRef}>
                <button
                  className="detail-overflow-trigger"
                  onClick={() => setOverflowOpen((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={overflowOpen}
                  aria-label="More actions"
                  title="More actions"
                >
                  &#8943;
                </button>
                {overflowOpen && (
                  <div className="detail-overflow-menu" role="menu">
                    <button
                      className="detail-overflow-item is-danger"
                      role="menuitem"
                      onClick={handleDeleteInvoice}
                    >
                      Delete Invoice
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Inline reject form for approvers — opens below the sticky bar so
              the rejection note has room without crowding the header. */}
          {!editing && canDecideApproval && showRejectForm && (
            <div className="detail-card detail-reject-card">
              <div className="detail-rejection-form">
                <input
                  type="text"
                  className="rejection-notes-input"
                  placeholder="Rejection reason (optional)"
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  autoFocus
                />
                <div className="detail-approval-actions">
                  <button
                    className="btn btn-reject"
                    onClick={() => handleApprovalRespond('rejected')}
                    disabled={respondingApproval}
                  >
                    {respondingApproval ? 'Rejecting...' : 'Confirm Reject'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowRejectForm(false); setRejectNotes(''); }}
                    disabled={respondingApproval}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {approvalMessage && (
            <div className={`doc-message doc-message-${approvalMessage.type}`}>
              {approvalMessage.text}
            </div>
          )}

          {/* Extraction Status Banner */}
          {invoice.extraction_status === 'pending' || invoice.extraction_status === 'processing' ? (
            <div className="extraction-banner">
              <span className="extraction-spinner"></span>
              AI is extracting invoice details...
            </div>
          ) : invoice.extraction_status === 'failed' ? (
            <div className="extraction-banner extraction-banner-failed">
              AI extraction failed. Please fill in the details manually.
            </div>
          ) : null}

          {/* Invoice Info */}
          <div className="detail-card">
            <h3 className="section-heading">Invoice Details</h3>

            <div className="detail-grid">
              <div className="detail-field">
                <label>Vendor</label>
                {editing ? (
                  <div className="vendor-combobox">
                    <input
                      type="text"
                      list="vendor-list"
                      ref={(el) => { editFieldRefs.current.vendor_id = el; }}
                      value={newVendorName || vendors.find(v => v.id === editFields.vendor_id)?.name || ''}
                      onChange={(e) => {
                        setNewVendorName(e.target.value);
                        const match = vendors.find(v => v.name.toLowerCase() === e.target.value.toLowerCase());
                        if (match) {
                          setEditFields({...editFields, vendor_id: match.id});
                          setNewVendorName('');
                        }
                      }}
                    />
                    <datalist id="vendor-list">
                      {vendors.map(v => <option key={v.id} value={v.name} />)}
                    </datalist>
                    {newVendorName && !vendors.find(v => v.name.toLowerCase() === newVendorName.toLowerCase()) && (
                      <button className="btn-create-vendor" onClick={async () => {
                        const res = await createVendor(newVendorName);
                        setVendors([...vendors, res.data]);
                        setEditFields({...editFields, vendor_id: res.data.id});
                        setNewVendorName('');
                      }}>
                        Create "{newVendorName}"
                      </button>
                    )}
                  </div>
                ) : (
                  editable('vendor_id',
                    <span>
                      {invoice.vendor_name || 'Not set'}
                      {invoice.vendor_match_reasoning && (
                        <>
                          {' '}
                          <ClassificationReasoning
                            kind="vendor"
                            reasoning={invoice.vendor_match_reasoning}
                          />
                        </>
                      )}
                    </span>
                  )
                )}
              </div>
              <div className="detail-field">
                <label>Amount</label>
                {editing ? (
                  <input type="number" step="0.01" value={editFields.amount}
                    ref={(el) => { editFieldRefs.current.amount = el; }}
                    onChange={e => setEditFields({...editFields, amount: e.target.value})} />
                ) : (
                  editable('amount',
                    <span>{invoice.amount != null ? formatCurrency(invoice.amount, invoice.currency) : 'Not set'}</span>
                  )
                )}
              </div>
              <div className="detail-field">
                <label>Currency</label>
                {editing ? (
                  <select value={editFields.currency}
                    ref={(el) => { editFieldRefs.current.currency = el; }}
                    onChange={e => setEditFields({...editFields, currency: e.target.value})}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                ) : (
                  editable('currency', <span>{invoice.currency || 'USD'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Invoice Number</label>
                {editing ? (
                  <input type="text" value={editFields.invoice_number}
                    ref={(el) => { editFieldRefs.current.invoice_number = el; }}
                    onChange={e => setEditFields({...editFields, invoice_number: e.target.value})} />
                ) : (
                  editable('invoice_number', <span className="code">{invoice.invoice_number || 'Not set'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Invoice Date</label>
                {editing ? (
                  <input type="date" value={editFields.invoice_date}
                    ref={(el) => { editFieldRefs.current.invoice_date = el; }}
                    onChange={e => setEditFields({...editFields, invoice_date: e.target.value})} />
                ) : (
                  editable('invoice_date', <span>{invoice.invoice_date || 'Not set'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Due Date</label>
                <span>{invoice.due_date || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <label>Source</label>
                <span>{invoice.source || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <label>Project</label>
                {editing ? (
                  <select value={editFields.project_id}
                    ref={(el) => { editFieldRefs.current.project_id = el; }}
                    onChange={e => setEditFields({...editFields, project_id: e.target.value})}>
                    <option value="">Select Project</option>
                    {candidateProjects.length > 0 && (
                      <optgroup label="Suggested Projects">
                        {candidateProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {p.source === 'vendor_mapping' ? 'vendor match' : 'AI suggestion'}
                            {p.default_gl_internal_order ? ` (${p.default_gl_internal_order})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="All Projects">
                      {projects
                        .filter(p => !candidateProjects.find(c => c.id === p.id))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </optgroup>
                  </select>
                ) : (
                  editable('project_id', <span>{invoice.project_name || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Owner</label>
                <span>{invoice.owner_name || 'N/A'}{editing && editFields.project_id && editFields.project_id !== invoice.project_id ? ' (will update from project)' : ''}</span>
              </div>
              {(invoice.classification_confidence != null || invoice.project_match_reasoning) && (
                <div className="detail-field">
                  <label>Classification Confidence</label>
                  <span>
                    {invoice.classification_confidence != null && (
                      <span className={`confidence-value ${
                        invoice.classification_confidence >= 0.8 ? 'confidence-high' :
                        invoice.classification_confidence >= 0.5 ? 'confidence-medium' : 'confidence-low'
                      }`}>
                        {Math.round(invoice.classification_confidence * 100)}%
                      </span>
                    )}
                    {invoice.project_match_reasoning && (
                      <>
                        {invoice.classification_confidence != null && ' '}
                        <ClassificationReasoning
                          kind="project"
                          reasoning={invoice.project_match_reasoning}
                        />
                      </>
                    )}
                  </span>
                </div>
              )}
              {invoice.description && (
                <div className="detail-field detail-field-full">
                  <label>Description</label>
                  <span>{invoice.description}</span>
                </div>
              )}
            </div>

            {/* GL Coding */}
            <h3 className="section-heading" style={{ marginTop: 8 }}>GL Coding</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Company Code</label>
                {editing ? (
                  <input type="text" value={editFields.gl_company_code}
                    ref={(el) => { editFieldRefs.current.gl_company_code = el; }}
                    onChange={e => setEditFields({...editFields, gl_company_code: e.target.value})}
                    placeholder="e.g. 1000" />
                ) : (
                  editable('gl_company_code', <span>{invoice.gl_company_code || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Cost Center</label>
                {editing ? (
                  <input type="text" value={editFields.gl_cost_center}
                    ref={(el) => { editFieldRefs.current.gl_cost_center = el; }}
                    onChange={e => setEditFields({...editFields, gl_cost_center: e.target.value})}
                    placeholder="e.g. CC-4200" />
                ) : (
                  editable('gl_cost_center', <span>{invoice.gl_cost_center || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>GL Account</label>
                {editing ? (
                  <input type="text" value={editFields.gl_account}
                    ref={(el) => { editFieldRefs.current.gl_account = el; }}
                    onChange={e => setEditFields({...editFields, gl_account: e.target.value})}
                    placeholder="e.g. 600100" />
                ) : (
                  editable('gl_account', <span className="code">{invoice.gl_account || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Internal Order</label>
                {editing ? (
                  <input type="text" value={editFields.gl_internal_order}
                    ref={(el) => { editFieldRefs.current.gl_internal_order = el; }}
                    onChange={e => setEditFields({...editFields, gl_internal_order: e.target.value})}
                    placeholder="e.g. IO-80001" />
                ) : (
                  editable('gl_internal_order', <span className="code">{invoice.gl_internal_order || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Budget line</label>
                {editing ? (
                  <select
                    value={editFields.budget_line_id}
                    ref={(el) => { editFieldRefs.current.budget_line_id = el; }}
                    onChange={e => setEditFields({...editFields, budget_line_id: e.target.value})}
                  >
                    <option value="">— none —</option>
                    {(() => {
                      const byCategory = {};
                      budgetLines.forEach((l) => {
                        const cat = l.category || 'Uncategorized';
                        if (!byCategory[cat]) byCategory[cat] = [];
                        byCategory[cat].push(l);
                      });
                      return Object.entries(byCategory).map(([cat, lines]) => (
                        <optgroup key={cat} label={cat}>
                          {lines.map((l) => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </select>
                ) : (
                  editable('budget_line_id',
                    <span>{invoice.budget_line_name || '—'}</span>
                  )
                )}
              </div>
            </div>

            {/* Additional Fields */}
            <h3 className="section-heading" style={{ marginTop: 8 }}>Additional Details</h3>
            <div className="detail-grid">
              <div className="detail-field">
                <label>Jurisdiction</label>
                {editing ? (
                  <input type="text" value={editFields.jurisdiction}
                    ref={(el) => { editFieldRefs.current.jurisdiction = el; }}
                    onChange={e => setEditFields({...editFields, jurisdiction: e.target.value})}
                    placeholder="e.g. US, LU, DE" />
                ) : (
                  editable('jurisdiction', <span>{invoice.jurisdiction || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field">
                <label>Payment Date</label>
                {editing ? (
                  <input type="date" value={editFields.payment_date}
                    ref={(el) => { editFieldRefs.current.payment_date = el; }}
                    onChange={e => setEditFields({...editFields, payment_date: e.target.value})} />
                ) : (
                  editable('payment_date', <span>{invoice.payment_date || 'N/A'}</span>)
                )}
              </div>
              <div className="detail-field detail-field-full">
                <label>Invoice Reference</label>
                {editing ? (
                  <input type="text" value={editFields.invoice_reference}
                    ref={(el) => { editFieldRefs.current.invoice_reference = el; }}
                    onChange={e => setEditFields({...editFields, invoice_reference: e.target.value})}
                    placeholder="URL or reference ID" />
                ) : (
                  editable('invoice_reference',
                    <span>
                      {invoice.invoice_reference ? (
                        /^https?:\/\//.test(invoice.invoice_reference) ? (
                          <a href={invoice.invoice_reference} target="_blank" rel="noopener noreferrer" className="btn-link" onClick={(e) => e.stopPropagation()}>
                            {invoice.invoice_reference}
                          </a>
                        ) : invoice.invoice_reference
                      ) : 'N/A'}
                    </span>
                  )
                )}
              </div>
              <div className="detail-field detail-field-full">
                <label>Comments</label>
                {editing ? (
                  <textarea
                    value={editFields.comments}
                    ref={(el) => { editFieldRefs.current.comments = el; }}
                    onChange={e => setEditFields({...editFields, comments: e.target.value})}
                    rows={3}
                    placeholder="Add comments..."
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit', fontSize: '0.95em' }}
                  />
                ) : (
                  editable('comments', <span>{invoice.comments || 'N/A'}</span>)
                )}
              </div>
              {showExtractedText && invoice.extracted_text && (
                <div className="detail-field detail-field-full">
                  <label
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setExtractedTextExpanded(!extractedTextExpanded)}
                  >
                    Extracted Text {extractedTextExpanded ? '▾' : '▸'}
                  </label>
                  {extractedTextExpanded && (
                    <textarea
                      value={invoice.extracted_text}
                      readOnly
                      rows={12}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        backgroundColor: '#f8f9fa',
                        resize: 'vertical',
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Save error surfaces here; Edit/Save/Cancel live in the sticky
                bar. Processors/approvers have no manual status control — the
                status is the read-only badge in the header. */}
            {statusError && (
              <div className="detail-actions">
                <span className="status-error">{statusError}</span>
              </div>
            )}

            {/* Admin-only manual status override (F7) — lets an admin place an
                invoice directly into a specific flow/state. */}
            {currentUser?.role === 'admin' && (
              <div className="status-change-section">
                <label>Status Override:</label>
                <select
                  value={invoice.status}
                  onChange={handleStatusChange}
                  disabled={statusUpdating}
                  className="status-select"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {statusUpdating && <span className="status-updating">Updating...</span>}
              </div>
            )}
          </div>

          {/* Project Allocations — visible post-approval; lets the AP user
              split the invoice across multiple projects in dollar amounts.
              The component decides on its own whether to render based on
              status, so we mount it unconditionally here. */}
          <AllocationsPanel invoiceId={id} invoiceStatus={invoice.status} />

          {/* Special Tax Handling — merged VAT + Luxembourg. A single
              segmented selector (None / VAT / Luxembourg) replaces the two
              "This is a…" checkboxes; the modes are mutually exclusive
              (selecting one clears the other; backend enforces too). Only the
              fields for the active mode are shown. Editable in edit mode. */}
          <div className="detail-card vat-section" id="vat-section">
            <h3 className="section-heading">Special Tax Handling</h3>

            <div className="tax-mode-row">
              <div className="tax-mode-segmented" role="radiogroup" aria-label="Special tax handling">
                {TAX_MODES.map((m) => {
                  const active = currentTaxMode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`tax-mode-option${active ? ' is-active' : ''}`}
                      disabled={!editing}
                      onClick={() => editing && setTaxMode(m.value)}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {currentTaxMode === 'none' && !editing && (
                <p className="empty-text" style={{ marginTop: 6 }}>
                  No special tax handling. Click Edit to mark this invoice as VAT
                  or Luxembourg.
                </p>
              )}
            </div>

            {/* ---- VAT mode ---- */}
            {currentTaxMode === 'vat' && (
              vatLoading && !vatSection ? (
                <p className="empty-text">Loading VAT details...</p>
              ) : (
                <>
                  {/* Below-threshold hint, driven by the real flag + live value. */}
                  {invoice.is_vat &&
                    vatSection &&
                    vatSection.vat_threshold_met === false && (
                      <div className="vat-below-threshold-hint">
                        Invoice total is below the{' '}
                        {vatSection.vat_threshold_amount != null
                          ? formatCurrency(
                              vatSection.vat_threshold_amount,
                              vatSection.vat_threshold_currency
                            )
                          : 'VAT'}{' '}
                        threshold, so this invoice stays on the normal approval flow.
                      </div>
                    )}

                  <div className="vat-section-grid">
                    <div className="detail-field">
                      <label>VAT Amount</label>
                      {editing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editFields.vat_amount}
                          onChange={(e) =>
                            setEditFields({ ...editFields, vat_amount: e.target.value })
                          }
                        />
                      ) : (
                        <span>
                          {vatSection?.vat_amount != null
                            ? formatCurrency(vatSection.vat_amount, vatSection.currency)
                            : 'Not set'}
                        </span>
                      )}
                    </div>
                    <div className="detail-field">
                      <label>Service Amount</label>
                      <span>
                        {vatSection?.service_amount != null
                          ? formatCurrency(vatSection.service_amount, vatSection.currency)
                          : 'Not set'}
                      </span>
                    </div>
                    <div className="detail-field">
                      <label>Invoice Total</label>
                      <span>
                        {vatSection?.invoice_total != null
                          ? formatCurrency(vatSection.invoice_total, vatSection.currency)
                          : 'Not set'}
                      </span>
                    </div>
                    <div className="detail-field">
                      <label>VAT Currency</label>
                      <span>{vatSection?.currency || invoice.currency || 'N/A'}</span>
                    </div>
                    <div className="detail-field">
                      <label>
                        Threshold Met
                        {thresholdReason && (
                          <>
                            {' '}
                            <ReasonChip kind="vat-threshold" text={thresholdReason} />
                          </>
                        )}
                      </label>
                      <span>{vatSection?.vat_threshold_met ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="detail-field">
                      <label>
                        Fiscal Year
                        {editing && (
                          <span style={{ color: '#666', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>
                            (override — defaults from invoice date)
                          </span>
                        )}
                      </label>
                      {editing ? (
                        <input
                          type="number"
                          min="1900"
                          max="2999"
                          step="1"
                          placeholder="e.g. 2025"
                          value={editFields.fiscal_year}
                          onChange={(e) =>
                            setEditFields({ ...editFields, fiscal_year: e.target.value })
                          }
                        />
                      ) : (
                        <span>{invoice.fiscal_year != null ? invoice.fiscal_year : 'Not set'}</span>
                      )}
                    </div>
                  </div>

                  <div className="vat-section-subhead">Vendor Bank Account</div>
                  {editing ? (
                    <div className="vat-bank-grid">
                      {[
                        ['bank_account_name', 'Account Name'],
                        ['bank_name', 'Bank'],
                        ['bank_location', 'Location'],
                        ['bank_swift_code', 'SWIFT'],
                        ['bank_iban', 'IBAN'],
                        ['bank_account_number', 'Account Number'],
                        ['bank_sort_code', 'Sort Code'],
                        ['bank_aba_number', 'ABA'],
                        ['bank_currency', 'Currency'],
                      ].map(([key, label]) => (
                        <div key={key}>
                          <div className="bank-label">{label}</div>
                          <input
                            type="text"
                            value={editFields[key] || ''}
                            onChange={(e) =>
                              setEditFields({ ...editFields, [key]: e.target.value })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : vatSection?.bank_account ? (
                    <div className="vat-bank-grid">
                      {vatSection.bank_account.account_name && (
                        <div>
                          <div className="bank-label">Account Name</div>
                          <div className="bank-value">{vatSection.bank_account.account_name}</div>
                        </div>
                      )}
                      {vatSection.bank_account.bank_name && (
                        <div>
                          <div className="bank-label">Bank</div>
                          <div className="bank-value">{vatSection.bank_account.bank_name}</div>
                        </div>
                      )}
                      {vatSection.bank_account.bank_location && (
                        <div>
                          <div className="bank-label">Location</div>
                          <div className="bank-value">{vatSection.bank_account.bank_location}</div>
                        </div>
                      )}
                      {vatSection.bank_account.swift_code && (
                        <div>
                          <div className="bank-label">SWIFT</div>
                          <div className="bank-value">{vatSection.bank_account.swift_code}</div>
                        </div>
                      )}
                      {vatSection.bank_account.iban && (
                        <div>
                          <div className="bank-label">IBAN</div>
                          <div className="bank-value">{vatSection.bank_account.iban}</div>
                        </div>
                      )}
                      {vatSection.bank_account.account_number && (
                        <div>
                          <div className="bank-label">Account Number</div>
                          <div className="bank-value">{vatSection.bank_account.account_number}</div>
                        </div>
                      )}
                      {vatSection.bank_account.sort_code && (
                        <div>
                          <div className="bank-label">Sort Code</div>
                          <div className="bank-value">{vatSection.bank_account.sort_code}</div>
                        </div>
                      )}
                      {vatSection.bank_account.aba_number && (
                        <div>
                          <div className="bank-label">ABA</div>
                          <div className="bank-value">{vatSection.bank_account.aba_number}</div>
                        </div>
                      )}
                      {vatSection.bank_account.currency && (
                        <div>
                          <div className="bank-label">Currency</div>
                          <div className="bank-value">{vatSection.bank_account.currency}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="empty-text">No bank account linked</p>
                  )}

                  {vatSection?.batch && (
                    <>
                      <div className="vat-section-subhead">Batch Assignment</div>
                      <div>
                        Batch <strong>{vatSection.batch.period}</strong>{' '}
                        ({vatSection.batch.status})
                        {vatSection.batch.package_generated_at && (
                          <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>
                            Packaged at {formatDateTime(vatSection.batch.package_generated_at)}
                          </span>
                        )}
                      </div>
                      <button
                        className="vat-batch-link"
                        onClick={() => navigate('/vat')}
                      >
                        Open VAT Dashboard →
                      </button>
                    </>
                  )}
                </>
              )
            )}

            {/* ---- Luxembourg mode ---- */}
            {currentTaxMode === 'luxembourg' && (
              <div style={{ marginTop: 12 }}>
                <div className="detail-field">
                  <label>
                    Detection Signal
                    {' '}
                    <ReasonChip kind="lux-detection" text={luxDetectionReason} />
                  </label>
                  <span>Luxembourg (CHS)</span>
                </div>
                {vatSection?.batch && (
                  <>
                    <div className="vat-section-subhead">Batch Assignment</div>
                    <div>
                      Batch <strong>{vatSection.batch.period}</strong>{' '}
                      ({vatSection.batch.status})
                      {vatSection.batch.package_generated_at && (
                        <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>
                          Packaged at {formatDateTime(vatSection.batch.package_generated_at)}
                        </span>
                      )}
                    </div>
                  </>
                )}
                <button
                  className="vat-batch-link"
                  onClick={() => navigate('/luxembourg')}
                  style={{ marginTop: 8 }}
                >
                  Open Luxembourg Dashboard →
                </button>
              </div>
            )}
          </div>

          {/* Event History */}
          <div className="detail-card">
            <h3 className="section-heading">Event History</h3>
            {invoice.events && invoice.events.length > 0 ? (
              <div className="timeline">
                {invoice.events.map((event) => (
                  <div className="timeline-item" key={event.id}>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="event-type-badge">{event.event_type}</span>
                        <span className="timeline-time">
                          {formatDateTime(event.created_at)}
                        </span>
                      </div>
                      {(event.from_status || event.to_status) && (
                        <div className="timeline-status-change">
                          <span
                            className="mini-badge"
                            style={{ backgroundColor: STATUS_COLORS[event.from_status] || '#ccc' }}
                          >
                            {event.from_status || '?'}
                          </span>
                          <span className="arrow">&#8594;</span>
                          <span
                            className="mini-badge"
                            style={{ backgroundColor: STATUS_COLORS[event.to_status] || '#ccc' }}
                          >
                            {event.to_status || '?'}
                          </span>
                        </div>
                      )}
                      {event.actor_name && (
                        <div className="timeline-actor">By: {event.actor_name}</div>
                      )}
                      {event.comment && (
                        <div className="timeline-comment">{event.comment}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No events recorded</p>
            )}
          </div>
        </div>
        </div>{/* end invoice-detail-left */}

        {/* Drag handle — resize preview pane */}
        <div
          className="invoice-detail-resizer"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize document preview"
          title="Drag to resize preview"
        >
          <div className="invoice-detail-resizer-grip" />
        </div>

        {/* Right Panel: Document Preview */}
        <div
          className="invoice-detail-right"
          style={{ flex: `0 0 ${previewWidth}%` }}
        >
          <div className="document-preview">
            <div className="document-preview-header">
              <div className="document-preview-header-left">
                <button
                  type="button"
                  className={`preview-expand-toggle${previewWidth >= 60 ? ' is-expanded' : ''}`}
                  onClick={togglePreviewWidth}
                  title={previewWidth >= 60 ? 'Shrink preview' : 'Expand preview'}
                  aria-label={previewWidth >= 60 ? 'Shrink preview' : 'Expand preview'}
                  aria-pressed={previewWidth >= 60}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    {previewWidth >= 60 ? (
                      <>
                        <path d="M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 3L2 8L7 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    ) : (
                      <>
                        <path d="M7 3L2 8L7 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 8H6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
                {documents.length > 1 ? (
                  <select
                    value={selectedDocIndex}
                    onChange={(e) => {
                      setSelectedDocIndex(Number(e.target.value));
                      setDocPreviewLoading(true);
                    }}
                  >
                    {documents.map((doc, idx) => (
                      <option key={doc.id} value={idx}>
                        {doc.original_filename || doc.filename || `Document ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="document-preview-filename">
                    {selectedDoc ? (selectedDoc.original_filename || selectedDoc.filename || 'Document') : 'Documents'}
                  </span>
                )}
              </div>
              {selectedDoc && (
                <a
                  href={getPreviewUrl(selectedDoc)}
                  className="btn-link"
                  download={selectedDoc.original_filename || selectedDoc.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </a>
              )}
            </div>

            {!selectedDoc ? (
              <div className="document-preview-empty">
                No documents uploaded
              </div>
            ) : isImage(selectedDoc.original_filename || selectedDoc.filename) ? (
              <>
                {docPreviewLoading && (
                  <div className="document-preview-loading">
                    <span className="extraction-spinner"></span>
                    Loading preview...
                  </div>
                )}
                <img
                  src={getPreviewUrl(selectedDoc)}
                  alt={selectedDoc.original_filename || selectedDoc.filename}
                  onLoad={() => setDocPreviewLoading(false)}
                  onError={() => setDocPreviewLoading(false)}
                  style={docPreviewLoading ? { display: 'none' } : {}}
                />
              </>
            ) : isPreviewable(selectedDoc.original_filename || selectedDoc.filename) ? (
              <>
                {docPreviewLoading && (
                  <div className="document-preview-loading">
                    <span className="extraction-spinner"></span>
                    Loading preview...
                  </div>
                )}
                <iframe
                  key={selectedDoc.id}
                  src={getPreviewUrl(selectedDoc)}
                  title={selectedDoc.original_filename || selectedDoc.filename || 'Document preview'}
                  onLoad={() => setDocPreviewLoading(false)}
                  style={docPreviewLoading ? { visibility: 'hidden', position: 'absolute' } : {}}
                />
              </>
            ) : isExcel(selectedDoc.original_filename || selectedDoc.filename) ? (
              excelLoading ? (
                <div className="document-preview-loading">
                  <span className="extraction-spinner"></span>
                  Loading preview...
                </div>
              ) : (
                <div className="excel-preview" dangerouslySetInnerHTML={{ __html: excelHtml }} />
              )
            ) : (
              <div className="document-preview-empty">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px' }}>
                    Preview not available for this file type
                  </div>
                  <a
                    href={getPreviewUrl(selectedDoc)}
                    className="btn btn-primary"
                    download={selectedDoc.original_filename || selectedDoc.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download {selectedDoc.original_filename || selectedDoc.filename}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>{/* end invoice-detail-right */}

        </div>{/* end invoice-detail-split */}
      </div>

      {/* GL Coding Confirmation / AI Instruction Modal */}
      {showGlModal && (
        <div className="gl-modal-overlay" onClick={() => { setShowGlModal(false); setGlPreview(null); setPendingGlSave(null); setSavingFields(false); setAiInstruction(''); }}>
          <div className="gl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gl-modal-header">
              <h2>{glPreview?.changes?.length > 0 ? 'Update GL Coding?' : 'Project Changed'}</h2>
              <button className="project-modal-close" onClick={() => { setShowGlModal(false); setGlPreview(null); setPendingGlSave(null); setSavingFields(false); setAiInstruction(''); }}>&times;</button>
            </div>
            <div className="gl-modal-body">
              {glPreview?.changes?.length > 0 && (
                <>
                  <p>Changing the project to <strong>{projects.find(p => p.id === (pendingGlSave?.project_id || ''))?.name || candidateProjects.find(p => p.id === (pendingGlSave?.project_id || ''))?.name || 'selected project'}</strong> would update these GL fields:</p>
                  <table className="gl-preview-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Current</th>
                        <th>New Value</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {glPreview.changes.map((field) => (
                        <tr key={field}>
                          <td className="gl-field-name">{
                            { gl_company_code: 'Company Code', gl_cost_center: 'Cost Center', gl_account: 'GL Account', gl_internal_order: 'Internal Order', jurisdiction: 'Jurisdiction' }[field] || field
                          }</td>
                          <td className="gl-current-value">{glPreview.current[field] || '(empty)'}</td>
                          <td className="gl-new-value">{glPreview.proposed[field]?.value || '(empty)'}</td>
                          <td><span className={`source-badge source-${(glPreview.proposed[field]?.source || '').replace(/_/g, '-')}`}>{glPreview.proposed[field]?.source || ''}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {!glPreview?.changes?.length && (
                <p>Assigning this invoice to <strong>{projects.find(p => p.id === (pendingGlSave?.project_id || ''))?.name || candidateProjects.find(p => p.id === (pendingGlSave?.project_id || ''))?.name || 'selected project'}</strong>.</p>
              )}
              {(invoice?.vendor_id || editFields.vendor_id) && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                    AI Instructions <span style={{ fontWeight: 'normal', color: '#666' }}>(optional)</span>
                  </label>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    How should the AI identify invoices from this vendor that belong to this project?
                  </p>
                  <textarea
                    value={aiInstruction}
                    onChange={(e) => setAiInstruction(e.target.value)}
                    placeholder="e.g., This vendor provides legal services for the highway expansion project..."
                    rows={3}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              )}
            </div>
            <div className="gl-modal-footer">
              {glPreview?.changes?.length > 0 ? (
                <>
                  <button className="btn btn-secondary" onClick={() => handleGlModalChoice(false)}>
                    No, keep current values
                  </button>
                  <button className="btn btn-primary" onClick={() => handleGlModalChoice(true)}>
                    Yes, update fields
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => { setShowGlModal(false); setGlPreview(null); setPendingGlSave(null); setSavingFields(false); setAiInstruction(''); }}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={() => handleGlModalChoice(true)}>
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceDetail;
